import { useState } from 'preact/hooks';
import { SuccessState } from './SuccessState';
import { AvatarStack } from './chorus';
import { chorusCount } from '../copy/counts';
import { OUTCOME_CONSENT_COPY, OUTCOME_CONSENT_VERSION } from '../copy/outcomeConsent';
import { guestApi } from '../api/client';
import { subscribeToPush } from '../lib/pushManager';
import styles from './GuestSuccessPanel.module.css';

export type SuccessTerminalKind =
  | 'coverageComplete'
  | 'phaseQuotaReached'
  | 'storyCompleted'
  | 'ordinary';

/**
 * What happens next, per terminal branch. The panel this replaces carried this
 * in its heading; the heading is now the same for everyone ("Your take is in."),
 * so the branch-specific fact moves here rather than being dropped — "more opens
 * up when this phase wraps" is the answer to a question the guest actually has.
 * `ordinary` says nothing because nothing is gated: they can simply add another.
 */
const WHATS_NEXT: Record<SuccessTerminalKind, string | null> = {
  coverageComplete: 'You covered every prompt in this phase.',
  phaseQuotaReached: 'More opens up when this phase wraps.',
  storyCompleted: 'This moment is finished.',
  ordinary: null,
};

interface GuestSuccessPanelProps {
  kind: SuccessTerminalKind;
  /** True when the take is sitting in the offline queue rather than posted. */
  queued: boolean;
  momentTitle?: string | null;
  contributionCount?: number;
  contributorCount?: number;
  /** Display names behind the avatar stack, most recent first. */
  contributorNames?: string[];
  /** Navigates to the thread — the payoff surface. */
  onViewChorus?: () => void;
  /**
   * Navigates to the finished artifact (the recap). Only meaningful once the
   * story is over, which is the one branch where the thread is no longer the
   * most interesting place to send someone.
   */
  onViewPayoff?: () => void;
  onDone?: () => void;
}

/**
 * The screen a contributor sees the moment their take lands.
 *
 * Three jobs, in the order the old panel failed at them: closure, a look at the
 * chorus they just joined, and an optional way to hear the ending. The panel it
 * replaces did the first for three of four terminal branches and, on `ordinary`
 * — the most common one — just closed the sheet with no acknowledgment at all.
 */
export function GuestSuccessPanel({
  kind,
  queued,
  momentTitle,
  contributionCount = 0,
  contributorCount = 0,
  contributorNames = [],
  onViewChorus,
  onViewPayoff,
  onDone,
}: GuestSuccessPanelProps) {
  const [email, setEmail] = useState('');
  const [contactState, setContactState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [contactDismissed, setContactDismissed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  // "is in" is a claim about the server. A queued take has not reached it, so the
  // offline branch says something that stays true until it syncs.
  const accent = queued ? 'is saved.' : 'is in.';
  const message = queued
    ? "It'll post the moment you're back online."
    : momentTitle
      ? `It's part of “${momentTitle}” now.`
      : "It's part of this moment now.";

  const whatsNext = WHATS_NEXT[kind];

  // The story is over, so the thread is no longer where the interesting thing is.
  const showPayoff = kind === 'storyCompleted' && Boolean(onViewPayoff);

  // Below two contributors, "see what everyone else added" would send someone to
  // look at their own take alone. The chorus CTA loses the primary slot there,
  // and the count gives way to the fact that actually holds. It also yields to
  // the payoff outright: once the story is finished, offering the thread beside
  // the Highlight is two primary buttons arguing over where to go next, and the
  // one that wins is the one that leads away from what was just made.
  const showChorus = !showPayoff && contributorCount >= 2 && Boolean(onViewChorus);

  // A lone contributor still needs somewhere to go. They just don't get sent to
  // "see what everyone else added" — there is no everyone else yet, and the
  // suppression above exists precisely so we don't march them to their own take
  // under a false promise. This is the same destination described honestly.
  const showSoloRoute = !showChorus && !showPayoff && Boolean(onViewChorus);

  const handleSaveContact = async (event: Event) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setContactState('saving');
    try {
      await guestApi.setOutcomeContact({
        email: trimmed,
        consentVersion: OUTCOME_CONSENT_VERSION,
      });
      setContactState('saved');
    } catch {
      setContactState('error');
    }
  };

  const handlePush = async () => {
    setPushBusy(true);
    // Scoped to outcomes: this button sits under "We'll send one message when
    // this moment is finished. Nothing else." — a sentence stored verbatim as a
    // versioned consent record. An unscoped subscription here would start
    // delivering every new take against that record.
    const ok = await subscribeToPush(['outcomes']);
    setPushBusy(false);
    if (ok) setContactState('saved');
  };

  const pushAvailable = typeof window !== 'undefined' && 'Notification' in window;

  return (
    <section class={styles.panel} aria-labelledby="guest-success-title">
      <div id="guest-success-title">
        <SuccessState title="Your take" accent={accent} message={message} />
      </div>

      {!showChorus && contributorCount <= 1 && (
        <p class={styles.firstTake}>You&rsquo;re the first take in this moment.</p>
      )}
      {whatsNext && <p class={styles.whatsNext}>{whatsNext}</p>}

      <div class={styles.stack}>
        {showChorus && (
          <div class={styles.card}>
            <div class={styles.chorusRow}>
              {contributorNames.length > 0 && (
                <AvatarStack names={contributorNames.slice(0, 4)} size={30} />
              )}
              <p class={styles.chorusCount}>
                {chorusCount(contributionCount, contributorCount)}
              </p>
            </div>
            <button type="button" class={styles.primaryBtn} onClick={onViewChorus}>
              See what everyone else added
            </button>
          </div>
        )}

        {showPayoff && (
          <button type="button" class={styles.primaryBtn} onClick={onViewPayoff}>
            Watch the Highlight
          </button>
        )}

        {showSoloRoute && (
          <button type="button" class={styles.btn} onClick={onViewChorus}>
            See it in the moment
          </button>
        )}

        {contactState === 'saved' ? (
          <p class={styles.saved} role="status">
            We&rsquo;ll let you know when this moment is finished.
          </p>
        ) : (
          !contactDismissed && (
            <div class={styles.card}>
              <p class={styles.cardTitle}>Want to see how it turns out?</p>
              <p class={styles.cardBody}>{OUTCOME_CONSENT_COPY}</p>
              <form class={styles.stackSm} onSubmit={handleSaveContact}>
                <input
                  type="email"
                  class={styles.field}
                  placeholder="Email (optional)"
                  aria-label="Email for the finished-moment message"
                  value={email}
                  onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
                />
                <button
                  type="submit"
                  class={showChorus || showPayoff ? styles.btn : styles.primaryBtn}
                  disabled={contactState === 'saving' || !email.trim()}
                >
                  {contactState === 'saving' ? 'Saving…' : "Tell me when it's done"}
                </button>
                {contactState === 'error' && (
                  <p class={styles.error} role="alert">
                    That didn&rsquo;t save. You can try again.
                  </p>
                )}
                {pushAvailable && (
                  <button type="button" class={styles.btn} onClick={handlePush} disabled={pushBusy}>
                    {pushBusy ? 'Setting up…' : 'Notify me here instead'}
                  </button>
                )}
                <button
                  type="button"
                  class={styles.ghostBtn}
                  onClick={() => setContactDismissed(true)}
                >
                  Not now
                </button>
              </form>
            </div>
          )
        )}

        {onDone && (
          <button type="button" class={styles.ghostBtn} onClick={onDone}>
            Done
          </button>
        )}
      </div>
    </section>
  );
}
